import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NgElement, WithProperties } from "@angular/elements";
import { BsModalService } from "ngx-bootstrap/modal";
import { AppState } from "../../core";
import { VolunteerTaskMapPoint } from "../models/volunteer-task-map-point";
import {
  LookupService,
  ScoutPointPhotoService,
  ScoutTaskService,
} from "../../core/services";
import { PathPlotterComponent } from "../../shared/mapping/path-plotter/path-plotter.component";
import { ScoutPointRefLauncher } from "../../core/services/scout-point-ref-launcher";
import { VolunteerTaskViewRecord } from "../models/volunteer-task-record";
import { VolunteerTaskPointMarkerPopupComponent } from "./volunteer-task-point-marker-popup/volunteer-task-point-marker-popup.component";
import { circleMarker, LatLngExpression, polyline } from "leaflet";
import L from "leaflet";
import { activity_status } from "../../core/models";

@Component({
  selector: "app-volunteer-task-map",
  templateUrl: "../../shared/mapping/path-plotter/path-plotter.component.html",
  styleUrls: [
    "../../shared/mapping/path-plotter/path-plotter.component.scss",
    "./volunteer-task-map.component.scss",
  ],
})
export class VolunteerTaskMapComponent
  extends PathPlotterComponent<VolunteerTaskMapPoint>
  implements OnInit, OnDestroy
{
  static readonly SELECTED_VOLUNTEER_MARKER_INFO_PANE = "selectedVolunteerMarkerInfo";
  static readonly VOLUNTEER_MARKER_PANE = "volunteerMarker";
  static readonly VOLUNTEER_MARKER_INFO_PANE = "volunteerMarkerInfo";

  @Input("mapPoints") set mapPointsSetter(mapPoints: VolunteerTaskMapPoint[]) {
    this.plotWhenReady(mapPoints);
  }

  @Input("selectedMapPoint") set selectedMapPointSetter(
    mapPoint: VolunteerTaskMapPoint
  ) {
    this.setSelectedPoint(mapPoint);
  }

  @Input()
  volunteerTask: VolunteerTaskViewRecord;

  @Input()
  bAllowPointSelection = true;

  constructor(
    appState: AppState,
    lookupService: LookupService,
    photoService: ScoutPointPhotoService,
    protected scoutService: ScoutTaskService,
    modalService: BsModalService,
    protected scoutPointRefLauncher: ScoutPointRefLauncher
  ) {
    super(appState, lookupService, photoService, modalService);
    this.markerPopupInitFn = this.volunteerTestPointMarkerPopupInitFn;
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getColorFn = (point: VolunteerTaskMapPoint, index: number) => point.color;

  volunteerTestPointMarkerPopupInitFn = (startPoint: VolunteerTaskMapPoint) => {
    return (fl) => {
      const popupComp: NgElement &
        WithProperties<VolunteerTaskPointMarkerPopupComponent> = document.createElement(
        "app-volunteer-task-point-marker-popup-element"
      ) as any;
      popupComp.addEventListener("closed", () =>
        document.body.removeChild(popupComp)
      );
      popupComp.pt = startPoint;
      popupComp.selectedPt = this.points[this.selectedIndex];
      popupComp.changeGuard = this.changeGuard;
      popupComp.deleteScoutPointFn = this.deletePointFn;
      popupComp.setSelectedPointFn = this.setSelectedPoint;
      popupComp.bShowSelectButton = this.bAllowPointSelection === true && (
        this.canSelectViaPoints || startPoint.type != "VIA");
      popupComp.showRefDataFn = this.showRefDataFn;
      popupComp.volunteerTask = this.volunteerTask;
      popupComp.allowEditMode = this.allowEditMode;
      popupComp.locked = this.locked;
      document.body?.appendChild(popupComp);
      const rtn: any = popupComp;
      return rtn;
    };
  };

  plotPathTest(
    volunteerTask: VolunteerTaskViewRecord,
    pathMapPoints: VolunteerTaskMapPoint[]
  ) {
    this.volunteerTask = volunteerTask;
    this.plot(pathMapPoints);
  }

  plotPathTestWhenReady(
    volunteerTask: VolunteerTaskViewRecord,
    pathMapPoints: VolunteerTaskMapPoint[]
  ) {
    this.volunteerTask = volunteerTask;
    this.plotWhenReady(pathMapPoints);
  }

  showRefDataFn = async (point: VolunteerTaskMapPoint) => {
    await this.scoutPointRefLauncher.showScoutPointRefData({
      buildingId: this.volunteerTask.buildingId,
      pathId: point.taskPoint?.pathId,
      pointId: point?.pointId,
      stage: this.volunteerTask.stage,
    });
  };

  //
  // rendering and style customizations
  //

  createPanes(map: L.Map) {
    super.createPanes(map);
    map.createPane(
      VolunteerTaskMapComponent.SELECTED_VOLUNTEER_MARKER_INFO_PANE
    ).style.zIndex = `${PathPlotterComponent.MARKER_Z_INDEX + 9}`;
    map.createPane(
      VolunteerTaskMapComponent.VOLUNTEER_MARKER_PANE
    ).style.zIndex = `${PathPlotterComponent.MARKER_Z_INDEX + 2}`;
    map.createPane(
      VolunteerTaskMapComponent.VOLUNTEER_MARKER_INFO_PANE
    ).style.zIndex = `${PathPlotterComponent.MARKER_Z_INDEX + 1}`;
  }

  getMarkerFillColor(pt: VolunteerTaskMapPoint) {
    return pt?.taskPoint ? "white" : "rgb(232,232,232,0.8)";
  }

  getSmallMarkerFillColor(pt: VolunteerTaskMapPoint, lineColor: string) {
    return pt?.taskPoint ? lineColor : "rgb(232,232,232,0.8)";
  }

  buildLabel(
    pt: VolunteerTaskMapPoint,
    context: { isViaPt: boolean; isHaPt: boolean }
  ) {
    var labelTxt = super.buildLabel(pt, context);
    const testPtOrder = 0; //TODO pt?.volunteerPoint?.order
    return testPtOrder ? `(#${testPtOrder}) ${labelTxt}` : labelTxt;
  }

  addInfoMarkerLabel(
    pt: VolunteerTaskMapPoint,
    ptState: { isViaPt: boolean; isHaPt: boolean; isSelected: boolean },
    extraMarkerOpts: Partial<L.MarkerOptions>
  ) {
    const ptLabelTxt = this.buildLabel(pt, ptState);
    extraMarkerOpts = { ...extraMarkerOpts };
    const isVolPt = !!pt?.taskPoint;
    if (isVolPt) {
      if (ptState.isSelected) {
        extraMarkerOpts.pane = VolunteerTaskMapComponent.SELECTED_VOLUNTEER_MARKER_INFO_PANE;
        extraMarkerOpts.opacity = 1;
      } else {
        extraMarkerOpts.pane = VolunteerTaskMapComponent.VOLUNTEER_MARKER_INFO_PANE;
      }
    } else {
      extraMarkerOpts.opacity = 0.2;
    }
    if (ptLabelTxt) {
      if (pt.latitude !== undefined && pt.longitude !== undefined) {
        const iconFragment = pt?.taskPoint
          ? '<i class="fa fa-star text-warning"></i>'
          : "";
        const divClasses = ["info-div"];
        if (ptState.isSelected) {
          divClasses.push("info-div-selected");
        }
        if (isVolPt) {
          divClasses.push("info-div-volunteer");
        }
        if ( this.isMuted(pt) ) {
          divClasses.push("disabled-point-tooltip");
        }
        const infoMarker = L.marker([pt.latitude, pt.longitude], {
          icon: new L.DivIcon({
            className: "info-div-icon",
            html: `<div class="${divClasses.join(
              " "
            )}">${iconFragment}${ptLabelTxt}</div>`,
          }),
          ...extraMarkerOpts,
        });
        this.infoMarkers.push(infoMarker);
      }
    }
  }

  override isMuted(pt: VolunteerTaskMapPoint) {
    const isMuted = super.isMuted(pt) ||
      ( pt?.taskPoint && [activity_status.Id.NOT_USING,activity_status.Id.ARCHIVED].includes(pt.taskPoint.status));
    return isMuted;
  }

  bindMarkerTooltip(
    marker: L.Marker,
    pt: VolunteerTaskMapPoint,
    opts: Partial<L.TooltipOptions>,
    isSelected: boolean
  ) {
    const isVolPt = !!pt?.taskPoint;
    opts = { ...opts };
    if (isVolPt) {
      opts.opacity = 1;
      if (!isSelected) {
        opts.pane = VolunteerTaskMapComponent.VOLUNTEER_MARKER_PANE;
      }
      opts.className += " volunteer-point-tooltip";
    } else {
      opts.opacity = 0.2;
    }
    if ( this.isMuted(pt) ) {
      opts.className += " disabled-point-tooltip";
    }
    (marker).bindTooltip(pt.name, opts);
  }

  buildCircleMarker(
    pt: VolunteerTaskMapPoint,
    latLng: LatLngExpression,
    markerOpts: L.CircleMarkerOptions,
    isSelected: boolean
  ) {
    markerOpts = { ...markerOpts };
    if (pt?.taskPoint) {
      if (!isSelected) {
        markerOpts.pane = VolunteerTaskMapComponent.VOLUNTEER_MARKER_PANE;
      }
      return super.buildCircleMarker(pt, latLng, markerOpts, isSelected);
    } else {
      markerOpts.opacity = 0.2;
      return circleMarker(latLng, markerOpts);
    }
  }

  addPathLine = (
    startPt: VolunteerTaskMapPoint,
    childPt: VolunteerTaskMapPoint,
    indexFromPathStart: number
  ) => {
    this.pathLines.push(
      polyline(
        [
          [startPt.latitude, startPt.longitude],
          [childPt.latitude, childPt.longitude],
        ],
        {
          color: this.getColorFn(childPt, indexFromPathStart),
          weight: 3,
          opacity: 0.2,
        }
      )
    );
  };
}
